import { createContext, useCallback, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { useSkus } from './hooks';
import { useSkuMutations } from './useSkuMutations';
import { useOrgClients } from '../hooks/useOrgClients';

export const Context = createContext();

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export const ContextProvider = ({ clientRef, children }) => {
    const { isCarrierAdmin, organizationId, orgClients } = useOrgClients();
    const [selectedEntity, setSelectedEntity] = useState('');

    const { skus, totalCount, loading, page, setPage, searchTerm, setSearchTerm, sortBy, setSortBy, refetch } =
        useSkus({ clientRef, isCarrierAdmin, organizationId, selectedClientId: selectedEntity });

    const { saveSku, deleteSku, importSkus, saving, deleting, importing } = useSkuMutations(clientRef);

    const [editModal, setEditModal] = useState(null); // null = closed, {} = add, row = edit
    const [deleteRow, setDeleteRow] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [notification, setNotification] = useState({});

    const notify = useCallback((severity, message) => setNotification({ severity, message }), []);

    // Shared try / catch / notify wrapper for the async operations.
    const runOp = useCallback(
        async (fn, errorMessage) => {
            try {
                return await fn();
            } catch (e) {
                console.error(e);
                captureException(e);
                notify('error', errorMessage);
                return undefined;
            }
        },
        [notify]
    );

    const handleSave = useCallback(
        (payload) => {
            const existing = editModal?.item_dimension_id ? editModal : null;
            return runOp(async () => {
                const { failedUploads = 0, failedRemovals = 0 } = (await saveSku({ ...payload, existing })) || {};
                setEditModal(null);
                const issues = [];
                if (failedUploads) issues.push(`${pluralize(failedUploads, 'file')} failed to upload`);
                if (failedRemovals) issues.push(`${pluralize(failedRemovals, 'file')} failed to remove`);
                if (issues.length) notify('warning', `SKU saved, but ${issues.join(' and ')}.`);
                else notify('success', 'SKU saved.');
            }, 'Could not save the SKU. Please try again.').finally(refetch);
        },
        [editModal, saveSku, runOp, notify, refetch]
    );

    const handleDelete = useCallback(() => {
        if (!deleteRow) return undefined;
        return runOp(async () => {
            await deleteSku(deleteRow);
            setDeleteRow(null);
            refetch();
            notify('success', 'SKU deleted.');
        }, 'Could not delete the SKU. Please try again.');
    }, [deleteRow, deleteSku, runOp, refetch, notify]);

    const handleImport = useCallback(
        (rows, skipped = 0, updateColumns = []) =>
            runOp(async () => {
                const count = await importSkus(rows, updateColumns);
                setImportOpen(false);
                refetch();
                const skippedMsg = skipped ? ` (${pluralize(skipped, 'row')} skipped)` : '';
                notify('success', `${pluralize(count, 'SKU')} imported${skippedMsg}.`);
            }, 'Could not import the CSV. Check the file and try again.'),
        [importSkus, runOp, refetch, notify]
    );

    const callbacks = useMemo(
        () => ({
            setPage,
            setSearchTerm,
            setSortBy,
            setSelectedEntity,
            openAdd: () => setEditModal({}),
            openEdit: (row) => setEditModal(row),
            closeEdit: () => setEditModal(null),
            requestDelete: (row) => setDeleteRow(row),
            cancelDelete: () => setDeleteRow(null),
            confirmDelete: handleDelete,
            openImport: () => setImportOpen(true),
            closeImport: () => setImportOpen(false),
            runImport: handleImport,
            save: handleSave,
            closeNotification: () => setNotification({}),
        }),
        [setPage, setSearchTerm, setSortBy, handleDelete, handleImport, handleSave]
    );

    const value = useMemo(
        () => ({
            state: {
                skus,
                totalCount,
                page,
                searchTerm,
                sortBy,
                editModal,
                deleteRow,
                importOpen,
                notification,
                isCarrierAdmin,
                orgClients,
                selectedEntity,
                currentClientId: clientRef,
            },
            loading: {
                init: loading,
                saving,
                deleting,
                importing,
            },
            callbacks,
        }),
        [
            skus,
            totalCount,
            page,
            searchTerm,
            sortBy,
            editModal,
            deleteRow,
            importOpen,
            notification,
            isCarrierAdmin,
            orgClients,
            selectedEntity,
            clientRef,
            loading,
            saving,
            deleting,
            importing,
            callbacks,
        ]
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
};
